import ApplicationController from "./application_controller"
import Logger from '../lib/logger'

/**
 * SearchFormController - Base controller for reusable search functionality
 * Provides common search features that can be extended by entity-specific controllers
 */
export default class extends ApplicationController {
  static targets = [
    "searchField", "filterField", "sortField", "submitButton",
    "autoSaveStatus", "autoSaveMessage"
  ]
  static values = {
    debounceMs: Number,
    autoSaveEnabled: Boolean,
    entityType: String
  }

  connect() {
    super.connect()
    Logger.debug('SearchFormController connecting', {
      entityType: this.entityTypeValue,
      debounceMs: this.debounceMsValue,
      autoSaveEnabled: this.autoSaveEnabledValue
    })
    this.debounceMsValue = this.debounceMsValue || 500
    this.autoSaveEnabledValue = this.autoSaveEnabledValue !== false

    this._boundDebounceSearch = this.debounceSearch.bind(this)
    this._boundHandleSubmit = this.handleSubmit.bind(this)

    this.setupSearchListeners()
    this.initializeSearchState()

    this._handleBeforeCache = () => {
      // Close any Bootstrap dropdowns within the form before caching
      // This prevents dropdowns from appearing open when navigating back in history
      this.element.querySelectorAll('.dropdown-menu.show').forEach(el => el.classList.remove('show'))
      this.element.querySelectorAll('.dropdown-toggle.show').forEach(el => {
        el.classList.remove('show')
        el.setAttribute('aria-expanded', 'false')
      })
    }
    document.addEventListener("turbo:before-cache", this._handleBeforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._handleBeforeCache)
    super.disconnect()
    clearTimeout(this.searchTimeout)
    this._searchFields?.forEach(field => {
      field.removeEventListener('input', this._boundDebounceSearch)
      field.removeEventListener('change', this._boundDebounceSearch)
    })
  }

  setupSearchListeners() {
    this._searchFields = Array.from(this.element.querySelectorAll('[data-action*="debounceSearch"]'))
    this._searchFields.forEach(field => {
      field.addEventListener('input', this._boundDebounceSearch)
      field.addEventListener('change', this._boundDebounceSearch)
    })

    this.element.addEventListener('submit', this._boundHandleSubmit)
  }

  initializeSearchState() {
    this.searchState = {}
  }

  debounceSearch(event) {
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // Set new timeout for search
    this.searchTimeout = setTimeout(() => {
      this.performSearch()
    }, this.debounceMsValue)

    // Show loading indicator
    this.showSearchLoading()
  }

  handleSubmit(event) {
    // Ensure we reset to page 1 on new search before native submission
    let pageInput = this.element.querySelector('input[name="page"]') || 
                    this.element.querySelector('input[name="q[page]"]')
    if (pageInput) {
      pageInput.value = '1'
    } else {
      pageInput = document.createElement('input')
      pageInput.type = 'hidden'
      pageInput.name = 'page'
      pageInput.value = '1'
      this.element.appendChild(pageInput)
    }

    // Do not prevent default. Let Turbo or the browser handle the form submission natively.
  }

  performSearch(event) {
    // Trigger native submission. The submit event will be caught by handleSubmit
    // which resets the page, and then Turbo will process the request natively.
    this.element.requestSubmit()
  }

  showSearchLoading() {
    // Show loading indicator
    const submitButton = this.element.querySelector('input[type="submit"]')
    if (submitButton) {
      const originalText = submitButton.value
      submitButton.value = 'Searching...'
      submitButton.disabled = true
      
      // Reset button after search completes
      setTimeout(() => {
        submitButton.value = originalText
        submitButton.disabled = false
      }, 1000)
    }
  }

  clearSearch() {
    // Clear all form fields
    this.element.reset()
    
    // Trigger search with empty fields to reload results
    this.performSearch()
  }

  // Advanced search methods
  searchByFilter(filterName, value) {
    const field = this.element.querySelector(`[name="${filterName}"]`)
    if (field) {
      if (field.type === 'checkbox') {
        field.checked = value
      } else {
        field.value = value
      }
      this.performSearch()
    }
  }

  searchBySort(sortValue) {
    const sortField = this.element.querySelector('[name*="sort"], [name*="s"]')
    if (sortField) {
      sortField.value = sortValue
      this.performSearch()
    }
  }

  // Form validation - can be overridden by subclasses
  validateForm() {
    let isValid = true
    const errors = []

    // Add specific validation logic in subclasses
    // This is a placeholder for common validation

    if (!isValid) {
      this.showErrors(errors)
    }

    return isValid
  }

  showErrors(errors) {
    // Remove existing error alerts
    this.element.querySelectorAll('.alert-danger').forEach(alert => alert.remove())

    // Create error alert
    const errorAlert = document.createElement('div')
    errorAlert.className = 'alert alert-danger alert-dismissible fade show'
    errorAlert.innerHTML = `
      <i class="fas fa-exclamation-triangle me-2"></i>
      <strong>Please fix the following errors:</strong>
      <ul class="mb-0 mt-2">
        ${errors.map(error => `<li>${error}</li>`).join('')}
      </ul>
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `

    // Insert at the top of the form
    this.element.insertBefore(errorAlert, this.element.firstChild)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (errorAlert.parentNode) {
        errorAlert.remove()
      }
    }, 5000)
  }
}